import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';

type RegistrationResponse =
  operations['getApiV1Registrations']['responses'][200]['content']['application/json'];
/** The CRM table requires the staff representation, not the learner projection. */
export type RegistrationCollectionResponse = Extract<
  RegistrationResponse,
  {
    readonly data: { readonly items: readonly { readonly cost: number | null }[] };
  }
>;

const text = z.string().nullable();
const related = z.object({ id: z.uuid(), name: text });
const registration = z.object({
  id: z.uuid(),
  registrationNumber: text,
  certified: z.boolean(),
  certifiedAt: text,
  certificationType: related.nullable(),
  completedRequirementIds: z.array(z.uuid()).max(100),
  completionForm: z.object({ signed: z.boolean().nullable() }),
  cost: z.number().nullable(),
  course: z
    .object({
      id: z.uuid(),
      name: text,
      number: text,
      startAt: text,
      endAt: text,
      status: text,
      type: related.nullable(),
      badgeImageUrl: text.optional(),
    })
    .nullable(),
  instructor: related.extend({ avatarUrl: text.optional() }).nullable(),
  instructorPaymentStatus: text,
  mainExam: z.object({
    attemptsUsed: z.number().int().nonnegative(),
    educationModule: related.nullable(),
    maximumAttempts: z.number().int().positive().nullable(),
    passedAt: text,
    state: z.enum(['completed', 'in_progress', 'not_started']),
  }),
  modules: z.object({
    completed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
  referenceType: text,
  registrationType: text,
  relatedDocuments: z.array(
    z.object({
      id: z.uuid(),
      deliveryState: z.literal('unavailable'),
      destination: z.literal('documents'),
      status: z
        .enum(['in-review', 'approved', 'not-valid', 'draft', 'cancelled', 'unknown'])
        .nullable(),
      type: z
        .enum([
          'certification-card',
          'paper-exam',
          'paper-completion-form',
          'first-aid-certificate',
          'medical-questionnaire',
          'student-liability-release',
          'instructor-insurance-waiver',
          'unknown',
        ])
        .nullable(),
      updatedAt: text,
    })
  ),
  school: related.nullable(),
  status: text,
  student: z
    .object({ id: z.uuid(), avatarUrl: text, firstName: text, lastName: text, name: text })
    .nullable(),
  studentPaymentStatus: text,
  submittedAt: text,
});

export const crmRegistrationResponseSchema = z.object({
  data: z.object({ items: z.array(registration).max(10) }),
  meta: z.object({
    countMode: z.literal('exact'),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    page: z.number().int().positive(),
    pageSize: z.union([z.literal(5), z.literal(10)]),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
}) satisfies z.ZodType<RegistrationCollectionResponse>;
